import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosModeration } from "../../../api/axios";
import Cookies from "cookies-js";
import { selectActiveSubprofileId } from "../../../sous-profile/redux/selectors/sousProfileSelectors";

/* -------------------------------------------------------------------------- */
/* 🚀 Helper: Normalize error to rejection payload                             */
/* -------------------------------------------------------------------------- */
const toRejection = (err, fallback = "Request failed") => {
  if (typeof err === "string") return err;
  const serverMessage = err?.response?.data?.message ?? err?.response?.data?.detail;
  return (Array.isArray(serverMessage) ? serverMessage.join(', ') : serverMessage) || err?.message || fallback;
};

/* -------------------------------------------------------------------------- */
/* 🚀 Helper: Get JWT token from cookies or localStorage                       */
/* -------------------------------------------------------------------------- */
const getAuthHeader = (state) => {
  const token = Cookies.get("accessToken") || "";
  const subprofileId = state ? selectActiveSubprofileId(state) : null;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(subprofileId ? { "x-subprofile-id": String(subprofileId) } : {}),
  };
};

/* -------------------------------------------------------------------------- */
/* 🚀 Moderation Thunks                                                      */
/* -------------------------------------------------------------------------- */

export const createModeratedPost = createAsyncThunk(
  "moderation/createPost",
  async (postData, { rejectWithValue, getState }) => {
    try {
      let photoBase64 = null;

      if (postData instanceof FormData) {
        const description = postData.get("description");
        const userId = postData.get("userId");
        const file = postData.get("file");
        const isAnonymous = postData.get("isAnonymous") === "true";

        if (file) {
          const uploadFormData = new FormData();
          uploadFormData.append("file", file);
          const uploadResponse = await axiosModeration.post(
            "/moderation/upload-image",
            uploadFormData,
            { headers: getAuthHeader(getState()) }
          );
          photoBase64 = uploadResponse.data?.base64 || null;
        }

        const payload = { description, user_id: userId, photo: photoBase64, isAnonymous };

        // ✅ Route corrigée : /moderation/posts (au lieu de /moderation/submit qui n'existe pas)
        const { data } = await axiosModeration.post("/moderation/posts", payload, {
          headers: getAuthHeader(getState()),
        });
        return data;
      }

      const payload = {
        description: postData.description,
        user_id: postData.userId,
        photo: postData.photo || null,
        isAnonymous: postData.isAnonymous ?? false,
      };

      // ✅ Route corrigée : /moderation/posts (au lieu de /moderation/submit qui n'existe pas)
      const { data } = await axiosModeration.post("/moderation/posts", payload, {
        headers: getAuthHeader(getState()),
      });
      return data;
    } catch (err) {
      return rejectWithValue(toRejection(err, "Failed to create post"));
    }
  }
);

export const fetchPosts = createAsyncThunk(
  "moderation/fetchPosts",
  async ({} = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosModeration.get(
        `/moderation/posts`,
        { headers: getAuthHeader() }
      );
      return data;
    } catch (err) {
      return rejectWithValue(toRejection(err, "Failed to fetch posts"));
    }
  }
);

export const fetchPostsByUser = createAsyncThunk(
  "moderation/fetchPostsByUser",
  async (userId, { rejectWithValue, getState }) => {
    try {
      const { data } = await axiosModeration.get(`/moderation/posts/user/${userId}`, {
        headers: getAuthHeader(getState()),
      });
      return data;
    } catch (err) {
      return rejectWithValue(toRejection(err, "Failed to fetch user's posts"));
    }
  }
);


export const fetchPostById = createAsyncThunk(
  "moderation/fetchPostById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosModeration.get(`/moderation/posts/${id}`, {
        headers: getAuthHeader(),
      });
      return data;
    } catch (err) {
      return rejectWithValue(toRejection(err, "Failed to fetch post by ID"));
    }
  }
);

export const fetchPostByCustomId = createAsyncThunk(
  "moderation/fetchPostByCustomId",
  async (idPost, { rejectWithValue }) => {
    try {
      const { data } = await axiosModeration.get(`/moderation/posts/by-post-id/${idPost}`, {
        headers: getAuthHeader(),
      });
      return data;
    } catch (err) {
      return rejectWithValue(toRejection(err, "Failed to fetch post by custom ID"));
    }
  }
);

export const fetchModerationStats = createAsyncThunk(
  "moderation/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosModeration.get("/moderation/stats", {
        headers: getAuthHeader(),
      });
      return data;
    } catch (err) {
      return rejectWithValue(toRejection(err, "Failed to fetch moderation stats"));
    }
  }
);




export const deletePostByCustomId = createAsyncThunk(
  "moderation/deletePostByCustomId",
  async (idPost, { rejectWithValue, getState }) => {
    try {
      const { data } = await axiosModeration.delete(
        `/moderation/posts/by-post-id/${idPost}`,
        { headers: getAuthHeader(getState()) }
      );
      return { idPost, ...data };
    } catch (err) {
      return rejectWithValue(toRejection(err, "Failed to delete post"));
    }
  }
);

/* ==================== ✅ AJOUT UNIQUE : UPDATE ==================== */
export const updatePostByCustomId = createAsyncThunk(
  "moderation/updatePostByCustomId",
  async ({ idPost, data }, { rejectWithValue, getState }) => {
    try {
      const { data: res } = await axiosModeration.put(
        `/moderation/posts/by-post-id/${idPost}`,
        data,
        { headers: getAuthHeader(getState()) }
      );
      return res;
    } catch (err) {
      return rejectWithValue(toRejection(err, "Failed to update post"));
    }
  }
);
